/**
 * Enhanced AdminClassTeacherUpdatePage using the new component system
 * Demonstrates advanced teacher assignment with current teacher info, teacher availability, and smart recommendations
 */

import React, { useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiBookOpen,
  FiCheck,
  FiSearch,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

// New component system imports
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Avatar,
  Badge,
  HStack,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  Card,
  ConfirmModal,
  Button as CustomButton,
  DataTable,
  FormField,
  FormPageLayout,
  StatsCard,
  TableColumn,
} from '@grcs/components';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import { useClassOperations, useUserOperations } from '@grcs/hook/useDatabase';
import { ClassProfile } from '@grcs/types/DataTypes';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface TeacherAssignmentStats {
  availableTeachers: number;
  totalTeachers: number;
  currentTeacher: string;
  classesAssigned: number;
  className: string;
  semesterYear: string;
}

interface EnhancedTeacherProfile extends UserProfile {
  assignment_status?: 'available' | 'assigned' | 'overloaded';
  current_classes?: number;
  current_class_names?: string[];
  recommended?: boolean;
  overloaded?: boolean;
  last_assignment?: string;
}

export const AdminClassTeacherUpdatePageEnhanced: React.FC = () => {
  const { supabase } = useData();
  const userOps = useUserOperations();
  const classOps = useClassOperations();
  const { classId, year } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [teachers, setTeachers] = useState<EnhancedTeacherProfile[]>([]);
  const [currentClass, setCurrentClass] = useState<ClassProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedTeacher, setSelectedTeacher] =
    useState<EnhancedTeacherProfile | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [stats, setStats] = useState<TeacherAssignmentStats>({
    availableTeachers: 0,
    totalTeachers: 0,
    currentTeacher: 'None Assigned',
    classesAssigned: 0,
    className: '',
    semesterYear: '',
  });

  // Load class and teacher data
  useEffect(() => {
    const loadData = async () => {
      if (!classId) return;

      try {
        setIsLoading(true);

        // Get class information
        const { data: classData } = await classOps.getClassInfoByClassId(
          classId,
        );
        if (classData && classData.length > 0) {
          const classInfo = classData[0] as ClassProfile;
          setCurrentClass(classInfo);

          // Get current teacher name if assigned
          let currentTeacherName = 'None Assigned';
          if (classInfo.teacher_token) {
            const { data: teacherData } =
              await userOps.getTeacherNameByTeacherToken(
                classInfo.teacher_token,
              );
            currentTeacherName =
              teacherData?.[0]?.full_name || 'Unknown Teacher';
          }

          setStats((prev) => ({
            ...prev,
            className: classInfo.class_name,
            semesterYear: classInfo.semester_year,
            currentTeacher: currentTeacherName,
          }));
        }

        // Get all teachers
        const { data: allTeachers } = await userOps.getUsersByRole(
          UserRole.TEACHER,
        );
        if (allTeachers) {
          // Get teacher class assignments in current year for analysis
          const { data: allClasses } = await supabase
            .from(TABLE_NAMES.CLASS)
            .select('teacher_token, class_name')
            .eq('semester_year', year)
            .eq('deleted', false);

          // Analyze teacher workloads
          const teacherWorkload = new Map<
            string,
            { classes: number; classNames: string[] }
          >();
          (allClasses || []).forEach((cls) => {
            if (cls.teacher_token) {
              if (!teacherWorkload.has(cls.teacher_token)) {
                teacherWorkload.set(cls.teacher_token, {
                  classes: 0,
                  classNames: [],
                });
              }
              const workload = teacherWorkload.get(cls.teacher_token)!;
              workload.classes += 1;
              workload.classNames.push(cls.class_name);
            }
          });

          const enhancedTeachers: EnhancedTeacherProfile[] = allTeachers.map(
            (teacher) => {
              const workload = teacherWorkload.get(teacher.user_token) || {
                classes: 0,
                classNames: [],
              };
              let assignmentStatus: EnhancedTeacherProfile['assignment_status'] =
                'available';

              if (workload.classes === 0) {
                assignmentStatus = 'available';
              } else if (workload.classes == 1) {
                assignmentStatus = 'assigned';
              } else {
                assignmentStatus = 'overloaded';
              }

              // Recommend teachers with lower workloads
              const recommended = workload.classes < 1;
              const overloaded = workload.classes > 1;

              return {
                ...teacher,
                assignment_status: assignmentStatus,
                current_classes: workload.classes,
                current_class_names: workload.classNames,
                recommended,
                overloaded,
                last_assignment: new Date().toISOString(), // Mock data - in real app, get from activity logs
              };
            },
          );

          // Sort by recommendation and availability
          enhancedTeachers.sort((a, b) => {
            if (a.recommended !== b.recommended) return a.recommended ? -1 : 1;
            return (a.current_classes || 0) - (b.current_classes || 0);
          });

          setTeachers(enhancedTeachers);

          // Update stats
          const available = enhancedTeachers.filter(
            (t) => t.assignment_status === 'available',
          ).length;
          const assigned = enhancedTeachers.filter(
            (t) => t.current_classes && t.current_classes > 0,
          ).length;

          setStats((prev) => ({
            ...prev,
            availableTeachers: available,
            totalTeachers: enhancedTeachers.length,
            classesAssigned: assigned,
          }));
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
        toast({
          title: 'Error Loading Data',
          description:
            error.message || 'Failed to load class and teacher information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [classId, classOps, userOps, supabase, toast]);

  // Filter teachers based on search
  const filteredTeachers = teachers.filter((teacher) => {
    if (searchTerm === '') return true;

    return (
      `${teacher.first_name} ${teacher.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      teacher.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Event handlers
  const handleAssignClick = (teacher: EnhancedTeacherProfile) => {
    setSelectedTeacher(teacher);
    setIsConfirmOpen(true);
  };

  const handleConfirmAssignment = async () => {
    if (!selectedTeacher || !classId) return;

    setIsAssigning(true);

    try {
      const { error } = await classOps.updateDataById(
        TABLE_NAMES.CLASS,
        'class_token',
        classId,
        { teacher_token: selectedTeacher.user_token },
      );

      if (error) {
        throw error;
      }

      toast({
        title: 'Teacher Assigned',
        description: `${selectedTeacher.first_name} ${selectedTeacher.last_name} has been assigned to ${stats.className}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate(`/admin/${year}/class/${classId}/students`);
    } catch (error: any) {
      console.error('Error assigning teacher:', error);
      toast({
        title: 'Assignment Error',
        description: error.message || 'Failed to assign teacher to class',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAssigning(false);
      setIsConfirmOpen(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/${year}/class/${classId}/students`);
  };

  // Table columns
  const columns: TableColumn<EnhancedTeacherProfile>[] = [
    {
      key: 'first_name',
      header: 'Teacher',
      render: (teacher: EnhancedTeacherProfile, value: string) => (
        <HStack spacing={3}>
          <Avatar
            size="sm"
            name={`${teacher.first_name} ${teacher.last_name}`}
            bg={
              teacher.recommended
                ? 'green.500'
                : teacher.assignment_status === 'available'
                ? 'blue.500'
                : 'orange.500'
            }
          />
          <div>
            <HStack spacing={2}>
              <div style={{ fontWeight: 600 }}>
                {teacher.first_name} {teacher.last_name}
              </div>
              {teacher.recommended && (
                <Badge colorScheme="green" size="sm">
                  Recommended
                </Badge>
              )}
            </HStack>
            <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
              {teacher.email}
            </div>
          </div>
        </HStack>
      ),
    },
    {
      key: 'phone',
      header: 'Contact',
      render: (teacher: EnhancedTeacherProfile, value: string) => (
        <Text fontSize="sm">{value || 'N/A'}</Text>
      ),
    },
    {
      key: 'current_classes',
      header: 'Current Workload',
      render: (teacher: EnhancedTeacherProfile, value: number) => (
        <VStack spacing={1} align="start">
          <Text fontSize="sm" fontWeight="500">
            {value || 0} class{(value || 0) !== 1 ? 'es' : ''}
          </Text>
          {teacher.current_class_names &&
            teacher.current_class_names.length > 0 && (
              <VStack spacing={1} align="start">
                {teacher.current_class_names
                  .slice(0, 2)
                  .map((className, index) => (
                    <Text key={index} fontSize="xs" color="gray.600">
                      {className}
                    </Text>
                  ))}
                {teacher.current_class_names.length > 2 && (
                  <Text fontSize="xs" color="gray.500">
                    +{teacher.current_class_names.length - 2} more
                  </Text>
                )}
              </VStack>
            )}
        </VStack>
      ),
    },
    {
      key: 'assignment_status',
      header: 'Availability',
      render: (teacher: EnhancedTeacherProfile, value: string) => {
        const statusConfig = {
          available: { color: 'green', label: 'Available' },
          assigned: { color: 'blue', label: 'Assigned' },
          overloaded: { color: 'red', label: 'Overloaded' },
        };

        const config =
          statusConfig[value as keyof typeof statusConfig] ||
          statusConfig.available;

        return (
          <Badge colorScheme={config.color} size="sm">
            {config.label}
          </Badge>
        );
      },
    },
    {
      key: 'user_token',
      header: 'Actions',
      render: (teacher: EnhancedTeacherProfile, value: string) => (
        <CustomButton
          size="sm"
          variant={
            currentClass?.teacher_token === value
              ? 'secondary'
              : teacher.overloaded
              ? 'danger'
              : teacher.recommended
              ? 'success'
              : 'primary'
          }
          onClick={() => handleAssignClick(teacher)}
          disabled={isAssigning}
        >
          {currentClass?.teacher_token === value ? (
            <FiCheck size={14} />
          ) : teacher.overloaded ? (
            <FiAlertCircle size={14} />
          ) : (
            'Assign'
          )}
        </CustomButton>
      ),
    },
  ];

  return (
    <>
      <FormPageLayout
        title={`Assign Teacher to ${stats.className}`}
        subtitle={`Select a teacher for ${stats.className} in ${
          year || stats.semesterYear
        }`}
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Classes', href: `/admin/${year}/class` },
            {
              label: stats.className,
              href: `/admin/${year}/class/${classId}/students`,
            },
            { label: 'Assign Teacher', isCurrent: true },
          ],
        }}
        onCancel={handleCancel}
        maxWidth="1200px"
        actions={
          <HStack spacing={3}>
            <CustomButton variant="secondary" onClick={handleCancel}>
              <FiX size={16} />
              Cancel
            </CustomButton>
          </HStack>
        }
      >
        <VStack spacing={6} align="stretch">
          {/* Current Assignment Alert */}
          {currentClass?.teacher_token && (
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <div>
                <AlertTitle>Current Assignment</AlertTitle>
                <AlertDescription>
                  {stats.currentTeacher} is currently assigned to this class.
                  Selecting a new teacher will replace the current assignment.
                </AlertDescription>
              </div>
            </Alert>
          )}

          {!currentClass?.teacher_token && (
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <div>
                <AlertTitle>No Teacher Assigned</AlertTitle>
                <AlertDescription>
                  This class currently has no teacher assigned. Please select a
                  teacher to enable class activities.
                </AlertDescription>
              </div>
            </Alert>
          )}

          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <StatsCard
              title="Available Teachers"
              value={stats.availableTeachers}
              icon={FiUser}
              variant="success"
              subtitle="Ready to assign"
            />

            <StatsCard
              title="Current Teacher"
              value={stats.currentTeacher}
              icon={FiCheck}
              variant={currentClass?.teacher_token ? 'info' : 'warning'}
              subtitle="Assigned to class"
            />

            <StatsCard
              title="Total Teachers"
              value={stats.totalTeachers}
              icon={FiUsers}
              variant="secondary"
              subtitle="In system"
            />

            <StatsCard
              title="Recommended"
              value={teachers.filter((t) => t.recommended).length}
              icon={FiBookOpen}
              variant="primary"
              subtitle="Low workload"
            />
          </SimpleGrid>

          {/* Search */}
          <Card padding={6}>
            <FormField
              label="Search Teachers"
              name="search"
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={setSearchTerm}
              icon={<FiSearch />}
            />
          </Card>

          {/* Teacher List */}
          <Card padding={6}>
            <VStack spacing={6} align="stretch">
              <div>
                <Text fontSize="xl" fontWeight="600" mb={2}>
                  Available Teachers
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Teachers with recommended badge have lower current workloads
                </Text>
              </div>

              <DataTable
                data={filteredTeachers}
                columns={columns}
                loading={isLoading}
                emptyState={
                  <VStack spacing={4} py={12}>
                    <FiUser size={48} opacity={0.4} />
                    <div style={{ textAlign: 'center' }}>
                      <p
                        style={{
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          marginBottom: '0.5rem',
                        }}
                      >
                        No teachers found
                      </p>
                      <p style={{ opacity: 0.8 }}>
                        {searchTerm
                          ? 'Try adjusting your search terms'
                          : 'No teachers available in the system'}
                      </p>
                    </div>
                  </VStack>
                }
              />
            </VStack>
          </Card>

          {/* Teacher Guidelines */}
          <Card padding={6} variant="outline">
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="600">
                Assignment Guidelines
              </Text>
              <VStack spacing={3} align="stretch">
                <HStack>
                  <Badge colorScheme="green" size="sm">
                    Recommended
                  </Badge>
                  <Text fontSize="sm">
                    Teachers with 1 or fewer current class assignments
                  </Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="green" size="sm">
                    Available
                  </Badge>
                  <Text fontSize="sm">
                    Teachers with no current assignments
                  </Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="blue" size="sm">
                    Assigned
                  </Badge>
                  <Text fontSize="sm">
                    Teachers with moderate workload (2 classes)
                  </Text>
                </HStack>
                <HStack>
                  <Badge colorScheme="red" size="sm">
                    Overloaded
                  </Badge>
                  <Text fontSize="sm">
                    Teachers with heavy workload (3+ classes)
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </Card>
        </VStack>
      </FormPageLayout>

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        type="info"
        title="Assign Teacher to Class"
        message={
          selectedTeacher
            ? `Assign ${selectedTeacher.first_name} ${selectedTeacher.last_name} to ${stats.className}?`
            : ''
        }
        description={
          selectedTeacher
            ? `This will ${
                currentClass?.teacher_token
                  ? 'replace the current teacher assignment'
                  : 'assign the first teacher to this class'
              }. The teacher will gain access to manage this class and its students.`
            : ''
        }
        onConfirm={handleConfirmAssignment}
        confirmText="Assign Teacher"
        loading={isAssigning}
      />
    </>
  );
};
